import { useState } from "react";
import {
  User,
  Globe,
  CreditCard,
  Bell,
  Info,
  Mic,
  ChevronRight,
  X,
  Check,
  Users,
  UsersRound,
  Radio,
  Megaphone,
  Brain,
  Crown,
  UserPlus,
  BarChart3,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { AppHeader } from "@/components/sartn/AppHeader";
import { FeatureStatusBanner } from "@/components/sartn/FeatureStatusBanner";
import { useAuthStore } from "@/stores/authStore";
import { useLanguageStore } from "@/stores/languageStore";
import { AnimatePresence, motion } from "framer-motion";
import { hapticFeedback } from "@/lib/telegram";

interface SettingsItemProps {
  icon: LucideIcon;
  label: string;
  labelAm: string;
  accentBg?: string;
  accentText?: string;
  onTap?: () => void;
  trailing?: React.ReactNode;
}

function localize(en: string, am: string, language: "en" | "am"): string {
  return language === "am" ? am : en;
}

function SettingsItem({ icon: Icon, label, labelAm, accentBg, accentText, onTap, trailing }: SettingsItemProps) {
  const language = useLanguageStore((state) => state.language);

  return (
    <button
      type="button"
      onClick={onTap}
      className={cn(
        "flex w-full items-center gap-3 px-4",
        "min-h-[44px] py-3",
        "transition-colors active:scale-[0.98] active:bg-[var(--color-bg)]",
      )}
    >
      <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-md)]", accentBg || "bg-[var(--color-bg)]")}>
        <Icon className={cn("h-[18px] w-[18px]", accentText || "text-[var(--color-text-secondary)]")} />
      </div>

      <div className="flex flex-1 flex-col items-start">
        <span className="text-sm font-medium text-[var(--color-text)]">
          {localize(label, labelAm, language)}
        </span>
      </div>

      {trailing ?? (
        <ChevronRight className="h-4 w-4 shrink-0 text-[var(--color-text-muted)]" />
      )}
    </button>
  );
}

interface ToggleSwitchProps {
  enabled: boolean;
  onToggle: () => void;
}

function ToggleSwitch({ enabled, onToggle }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full",
        "transition-colors duration-200",
        enabled ? "bg-[var(--color-primary)]" : "bg-[var(--color-border)]",
      )}
    >
      <span
        className={cn(
          "inline-block h-4 w-4 rounded-full bg-white shadow-sm",
          "transition-transform duration-200",
          enabled ? "translate-x-6" : "translate-x-1",
        )}
      />
    </button>
  );
}

interface SettingsGroupProps {
  title: string;
  titleAm: string;
  children: React.ReactNode;
}

function SettingsGroup({ title, titleAm, children }: SettingsGroupProps) {
  const language = useLanguageStore((state) => state.language);

  return (
    <div className="space-y-1.5">
      <div className="px-1">
        <h2 className="font-headline text-xs font-bold uppercase tracking-wide text-[var(--color-text-secondary)]">
          {localize(title, titleAm, language)}
        </h2>
      </div>

      <div
        className={cn(
          "overflow-hidden rounded-[var(--radius-lg)]",
          "shadow-sm",
          "bg-[var(--color-surface)]",
          "divide-y divide-[var(--color-border-subtle)]",
        )}
      >
        {children}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [activeModal, setActiveModal] = useState<"profile" | "payments" | "about" | null>(null);

  const liveUser = useAuthStore((s) => s.user);
  const user = liveUser;

  const language = useLanguageStore((state) => state.language);
  const toggleLanguage = useLanguageStore((state) => state.toggleLanguage);

  const [teleAcc, setTeleAcc] = useState(() => localStorage.getItem("sartn_pay_telebirr_acc") || "");
  const [teleNo, setTeleNo] = useState(() => localStorage.getItem("sartn_pay_telebirr_no") || "");
  const [cbeAcc, setCbeAcc] = useState(() => localStorage.getItem("sartn_pay_cbe_acc") || "");
  const [cbeNo, setCbeNo] = useState(() => localStorage.getItem("sartn_pay_cbe_no") || "");
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  function handleSavePayments() {
    hapticFeedback("notification", undefined, "success");
    localStorage.setItem("sartn_pay_telebirr_acc", teleAcc);
    localStorage.setItem("sartn_pay_telebirr_no", teleNo);
    localStorage.setItem("sartn_pay_cbe_acc", cbeAcc);
    localStorage.setItem("sartn_pay_cbe_no", cbeNo);
    setShowSaveSuccess(true);
    setTimeout(() => {
      setShowSaveSuccess(false);
      setActiveModal(null);
    }, 1500);
  }

  function handleLanguageToggle() {
    hapticFeedback("selection");
    toggleLanguage();
  }

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title={language === "am" ? "ቅንብሮች" : "Settings"} />

      <div className="flex flex-col gap-6 px-4 pt-4 pb-24">
        <FeatureStatusBanner
          status="limited"
          title="Some settings are local / አንዳንድ ቅንብሮች በመሣሪያ ላይ ብቻ ናቸው"
          description="Language is saved locally; payment account fields are not synced to the backend yet. / ቋንቋ በመሣሪያው ላይ ይቀመጣል፤ የክፍያ መለያዎች ገና ከbackend ጋር አይመሳሰሉም።"
        />

        <SettingsGroup title="Account" titleAm="መለያ">
          <SettingsItem
            icon={User}
            label="Profile"
            labelAm="መገለጫ"
            accentBg="bg-blue-50"
            accentText="text-blue-600"
            onTap={() => {
              hapticFeedback("selection");
              setActiveModal("profile");
            }}
          />
          <SettingsItem
            icon={Globe}
            label="Language"
            labelAm="ቋንቋ"
            accentBg="bg-emerald-50"
            accentText="text-emerald-600"
            onTap={handleLanguageToggle}
            trailing={
              <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded-full">
                {language === "am" ? "አማርኛ" : "English"}
              </span>
            }
          />
        </SettingsGroup>

        <SettingsGroup title="Features" titleAm="ባህሪያት">
          <SettingsItem
            icon={Mic}
            label="Voice"
            labelAm="ድምጽ"
            accentBg="bg-violet-50"
            accentText="text-violet-600"
            onTap={() => {
              hapticFeedback("selection");
              window.location.hash = "/settings/voice";
            }}
          />
          <SettingsItem
            icon={CreditCard}
            label="Payments"
            labelAm="ክፍያዎች"
            accentBg="bg-amber-50"
            accentText="text-amber-600"
            onTap={() => {
              hapticFeedback("selection");
              setActiveModal("payments");
            }}
          />
        </SettingsGroup>

        <SettingsGroup title="Tools" titleAm="መሣሪያዎች">
          {[
            { icon: Users, label: "Contacts", labelAm: "እውቂያዎች", path: "/contacts", accentBg: "bg-sky-50", accentText: "text-sky-600" },
            { icon: UsersRound, label: "Groups", labelAm: "ቡድኖች", path: "/groups", accentBg: "bg-teal-50", accentText: "text-teal-600" },
            { icon: Radio, label: "Channels", labelAm: "ቻናሎች", path: "/channels", accentBg: "bg-indigo-50", accentText: "text-indigo-600" },
            { icon: Megaphone, label: "Marketing", labelAm: "ግብይት", path: "/marketing", accentBg: "bg-rose-50", accentText: "text-rose-600" },
            { icon: Brain, label: "Intelligence", labelAm: "ትንተና", path: "/intelligence", accentBg: "bg-purple-50", accentText: "text-purple-600" },
            { icon: Crown, label: "Subscriptions", labelAm: "ደንበኝነት", path: "/subscriptions", accentBg: "bg-yellow-50", accentText: "text-yellow-600" },
            { icon: UserPlus, label: "Team", labelAm: "ቡድን", path: "/team", accentBg: "bg-cyan-50", accentText: "text-cyan-600" },
            { icon: BarChart3, label: "Market", labelAm: "ገበያ", path: "/market", accentBg: "bg-orange-50", accentText: "text-orange-600" },
          ].map((tool) => (
            <SettingsItem
              key={tool.path}
              icon={tool.icon}
              label={tool.label}
              labelAm={tool.labelAm}
              accentBg={tool.accentBg}
              accentText={tool.accentText}
              onTap={() => {
                hapticFeedback("selection");
                window.location.hash = tool.path;
              }}
            />
          ))}
        </SettingsGroup>

        <SettingsGroup title="Preferences" titleAm="ምርጫዎች">
          <SettingsItem
            icon={Bell}
            label="Notifications"
            labelAm="ማሳወቂያዎች"
            accentBg="bg-red-50"
            accentText="text-red-600"
            trailing={
              <ToggleSwitch
                enabled={notificationsEnabled}
                onToggle={() => {
                  hapticFeedback("selection");
                  setNotificationsEnabled((prev) => !prev);
                }}
              />
            }
          />
        </SettingsGroup>

        <SettingsGroup title="Info" titleAm="መረጃ">
          <SettingsItem
            icon={Info}
            label="About"
            labelAm="ስለ"
            accentBg="bg-slate-50"
            accentText="text-slate-600"
            onTap={() => {
              hapticFeedback("selection");
              setActiveModal("about");
            }}
          />
        </SettingsGroup>
      </div>

      <AnimatePresence>
        {activeModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            {activeModal === "profile" && (
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: "spring", stiffness: 350, damping: 28 }}
                className="w-full max-w-sm rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-5 shadow-2xl relative"
              >
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="absolute top-4 right-4 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                >
                  <X className="h-5 w-5" />
                </button>

                <div className="flex flex-col items-center gap-4 mt-2">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--color-primary)] text-3xl font-extrabold text-white shadow-inner">
                    {user?.first_name?.[0]?.toUpperCase() ?? "?"}
                  </div>

                  <div className="text-center">
                    <h3 className="text-lg font-bold text-[var(--color-text)]">
                      {user ? `${user.first_name} ${user.last_name || ""}` : "Telegram profile unavailable"}
                    </h3>
                    <p className="text-sm text-[var(--color-text-muted)]">
                      {user
                        ? `@${user.username || "no_username"}`
                        : localize("Open inside Telegram", "በቴሌግራም ውስጥ ይክፈቱ", language)}
                    </p>
                  </div>

                  {user?.is_premium && (
                    <span className="text-[10px] font-bold tracking-wider uppercase text-blue-700 bg-blue-50 px-3 py-1 rounded-full">
                      Telegram Premium
                    </span>
                  )}

                  <div className="w-full border-t border-[var(--color-border-subtle)] pt-4 mt-2 space-y-2.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-muted)]">User ID</span>
                      <span className="font-mono text-[var(--color-text)]">{user?.id ?? "Unavailable"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-muted)]">Language Code</span>
                      <span className="text-[var(--color-text)] uppercase">{user?.language_code || "unknown"}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activeModal === "payments" && (
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: "spring", stiffness: 350, damping: 28 }}
                className="w-full max-w-sm rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-5 shadow-2xl relative"
              >
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="absolute top-4 right-4 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                >
                  <X className="h-5 w-5" />
                </button>

                <h3 className="text-base font-bold text-[var(--color-text)] mb-4">
                  {language === "am" ? "የአካባቢ ክፍያ መረጃ" : "Local Payment Display Info"}
                </h3>

                <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {localize(
                    "Saved on this device only. Backend payment-method sync is pending.",
                    "በዚህ መሣሪያ ላይ ብቻ ይቀመጣል፤ backend ማመሳሰል ገና ነው።",
                    language,
                  )}
                </p>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-amber-600 uppercase tracking-wider">TeleBirr</label>
                    <input
                      type="text"
                      value={teleAcc}
                      onChange={(e) => setTeleAcc(e.target.value)}
                      placeholder="Account Holder Name"
                      className="w-full bg-[var(--color-bg)] rounded-lg px-3 py-2 text-sm text-[var(--color-text)] shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                    />
                    <input
                      type="text"
                      value={teleNo}
                      onChange={(e) => setTeleNo(e.target.value)}
                      placeholder="Mobile Phone Number"
                      className="w-full bg-[var(--color-bg)] rounded-lg px-3 py-2 text-sm text-[var(--color-text)] shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-blue-600 uppercase tracking-wider">CBE Bank</label>
                    <input
                      type="text"
                      value={cbeAcc}
                      onChange={(e) => setCbeAcc(e.target.value)}
                      placeholder="Account Holder Name"
                      className="w-full bg-[var(--color-bg)] rounded-lg px-3 py-2 text-sm text-[var(--color-text)] shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                    />
                    <input
                      type="text"
                      value={cbeNo}
                      onChange={(e) => setCbeNo(e.target.value)}
                      placeholder="CBE Account Number"
                      className="w-full bg-[var(--color-bg)] rounded-lg px-3 py-2 text-sm text-[var(--color-text)] shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveModal(null)}
                    className="px-4 py-2 rounded-lg text-xs font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSavePayments}
                    className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                  >
                    {showSaveSuccess ? (
                      <>
                        <Check className="h-3.5 w-3.5" />
                        Saved locally
                      </>
                    ) : (
                      "Save Locally"
                    )}
                  </button>
                </div>
              </motion.div>
            )}

            {activeModal === "about" && (
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ type: "spring", stiffness: 350, damping: 28 }}
                className="w-full max-w-sm rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-5 shadow-2xl relative"
              >
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="absolute top-4 right-4 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                >
                  <X className="h-5 w-5" />
                </button>

                <h3 className="text-base font-bold text-[var(--color-text)] mb-2">SARTN Suite</h3>
                <p className="text-xs text-blue-600 font-semibold mb-4">Version 0.4.0 (Amharic-First AI)</p>

                <div className="space-y-3 text-xs text-[var(--color-text-secondary)] leading-relaxed">
                  <p>
                    SARTN is Ethiopia's first complete Telegram-native business operating system.
                    It empowers merchants to automate chat sales, coordinate deliveries, track orders,
                    verify payments, schedule channel content, and monitor competitors effortlessly.
                  </p>
                  <p className="text-[10px] text-[var(--color-text-muted)]">
                    Designed for maximum performance and security inside the Telegram WebApp sandbox.
                  </p>
                </div>

                <div className="mt-5 border-t border-[var(--color-border-subtle)] pt-4 flex justify-between text-[10px]">
                  <a
                    href="#/privacy"
                    onClick={() => setActiveModal(null)}
                    className="text-blue-600 hover:underline"
                  >
                    Privacy Policy
                  </a>
                  <a
                    href="#/terms"
                    onClick={() => setActiveModal(null)}
                    className="text-blue-600 hover:underline"
                  >
                    Terms of Service
                  </a>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
